/**
 * useEntityDetailPanel — State management hook for the Entity Detail Panel
 *
 * Manages: active tab, dock position, panel size, fullscreen mode.
 * Persists preferences in localStorage.
 */

import { useState, useCallback, useEffect } from 'react';
import type {
  DetailTab,
  DockPosition,
  PanelSize,
  TabOrientation,
  EntityDetailPanelState,
} from './types';
import { getStorageKey } from './types';

// ============================================================================
// LOCALSTORAGE HELPERS
// ============================================================================

function loadPreference<T>(key: string, fallback: T): T {
  try {
    const stored = localStorage.getItem(getStorageKey(key));
    return stored ? (JSON.parse(stored) as T) : fallback;
  } catch {
    return fallback;
  }
}

function savePreference<T>(key: string, value: T): void {
  try {
    localStorage.setItem(getStorageKey(key), JSON.stringify(value));
  } catch {
    // localStorage may be full or blocked
  }
}

// ============================================================================
// HOOK
// ============================================================================

export function useEntityDetailPanel(): EntityDetailPanelState {
  const [activeTab, setActiveTabRaw] = useState<DetailTab>('details');
  const [dockPosition, setDockPositionRaw] = useState<DockPosition>(
    () => loadPreference<DockPosition>('dock', 'right')
  );
  const [panelSize, setPanelSizeRaw] = useState<PanelSize>(
    () => loadPreference<PanelSize>('size', 'standard')
  );
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [tabOrientation, setTabOrientationRaw] = useState<TabOrientation>(
    () => loadPreference<TabOrientation>('tabOrientation', 'horizontal')
  );

  // Persist preferences on change
  const setDockPosition = useCallback((pos: DockPosition) => {
    setDockPositionRaw(pos);
    savePreference('dock', pos);
  }, []);

  const setPanelSize = useCallback((size: PanelSize) => {
    setPanelSizeRaw(size);
    savePreference('size', size);
    // Exit fullscreen if changing to non-fullscreen size
    if (size !== 'fullscreen') {
      setIsFullscreen(false);
    }
  }, []);

  const setActiveTab = useCallback((tab: DetailTab) => {
    setActiveTabRaw(tab);
  }, []);

  const toggleFullscreen = useCallback(() => {
    setIsFullscreen(prev => !prev);
  }, []);

  const toggleTabOrientation = useCallback(() => {
    setTabOrientationRaw(prev => {
      const next = prev === 'horizontal' ? 'vertical' : 'horizontal';
      savePreference('tabOrientation', next);
      return next;
    });
  }, []);

  // Escape key exits fullscreen
  useEffect(() => {
    if (!isFullscreen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsFullscreen(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isFullscreen]);

  return {
    activeTab,
    setActiveTab,
    dockPosition,
    setDockPosition,
    panelSize,
    setPanelSize,
    isFullscreen,
    toggleFullscreen,
    tabOrientation,
    toggleTabOrientation,
  };
}
